/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import EnvelopeWithBox from '../EnvelopeWithBox';
import Styles from '../../css/LoanInformation.module.css';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import { useLoanContext } from '../../contexts/LoanContext';
import { NumericFormat } from 'react-number-format';
import { addMonthsToDate, getTimestampAsDate, safeFullName } from '../../common/UtilityFunctions';
import LoanHeloc from './LoanHeloc';

const BorrowerLoanInformation = () => {
  const { loanHeloc } = useLoanContext();
  const { propData, lnptData, loanData, loanEvents, borrowersArr } = useLoanInformationContext();
  const currentPrimaryNumber = (loanData?.primaryBorrowerNumber ?? loanData?.primaryBorrowerNumber) || null;

  const borrInfo = (borrower: BorrowerData, idx: number) => {
    const isPrimary = borrower.borrowerNumber === currentPrimaryNumber;
    return (
      <tr key={borrower.borrowerNumber ?? `borrower_${idx}`}>
        <th>{isPrimary ? 'Primary Borrower' : `Co-borrower ${idx}`}</th>
        <td>{borrower.fullName || safeFullName(borrower) || ''}</td>
      </tr>
    );
  };

  return (
    <>
      <EnvelopeWithBox blockName='Loan Information'>
        <div className={Styles.loanInfoTable}>
          <div className={Styles.centered}>
            <table className={Styles.loanInfoTableSection}>
              <tbody>
                {borrowersArr
                  .filter((b) => !!b && !!b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
                  .filter((b) => b.borrowerNumber === currentPrimaryNumber)
                  .map((borrower, idx) => borrInfo(borrower, idx))}
                {borrowersArr
                  .filter((b) => !!b && !!b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
                  .filter((b) => b.borrowerNumber != currentPrimaryNumber)
                  .map((borrower, idx) => borrInfo(borrower, idx + 1))}
                <tr>
                  <th>Property Address</th>
                  <td>{propData?.fullAddress}</td>
                </tr>
                <tr>
                  <th>Mailing Address</th>
                  <td>
                    {loanData?.mailingAddress +
                      ', ' +
                      loanData?.mailingUnitNumber +
                      ', ' +
                      loanData?.mailingCity +
                      ', ' +
                      loanData?.mailingState +
                      ', ' +
                      loanData?.mailingZipCode}
                  </td>
                </tr>
                <tr>
                  <th>Current Loan Balance</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={lnptData?.upb ? lnptData?.upb : 0}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <th>Current Escrow Balance</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={lnptData?.escrowRecdBal ? lnptData?.escrowRecdBal : 0}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <th>Interest Paid Year-To-Date</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={lnptData?.interestPaidYTD ? lnptData?.interestPaidYTD : 0}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <th>Current Interest Rate</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={lnptData?.intRate ? lnptData?.intRate : 0}
                      decimalScale={3}
                      fixedDecimalScale
                      suffix='%'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <th>Loan Paid-To-Date</th>
                  <td>{loanData?.lpi ? getTimestampAsDate(loanData?.lpi) : ''}</td>
                </tr>
                <tr>
                  <th>First Payment Date</th>
                  <td>{loanData?.firstPaymentDate ? getTimestampAsDate(loanData?.firstPaymentDate) : ''}</td>
                </tr>
                <tr>
                  <th>Maturity Date</th>
                  <td>
                    {lnptData?.remainingTerm === 0 || !loanData?.firstPaymentDate
                      ? getTimestampAsDate(
                          new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime() / 1000,
                        )
                      : addMonthsToDate(loanData?.firstPaymentDate, loanData?.loanTerm - 1)}
                  </td>
                </tr>
                <tr>
                  <th>Appraised Value</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={loanData?.apprReqAmt ? loanData?.apprReqAmt : 0}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <th>Mortgage Term (months)</th>
                  <td>{loanData?.loanTerm ? loanData?.loanTerm : 0}</td>
                </tr>
                <tr>
                  <th>Closed on</th>
                  <td>{loanEvents?.SDT ? getTimestampAsDate(loanEvents?.SDT?.activeDate) : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </EnvelopeWithBox>
      {loanHeloc && <LoanHeloc></LoanHeloc>}
    </>
  );
};

export default BorrowerLoanInformation;
